"use client";

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { RequestAuditModal } from "@/components/RequestAuditModal";

type RequestAuditContextValue = {
  open: () => void;
  close: () => void;
};

const RequestAuditContext = createContext<RequestAuditContextValue | null>(null);

/**
 * Opens the "Request a Security Audit" modal from anywhere in the tree.
 *
 * This used to also intercept clicks on any `<a href="/request-audit">`, which
 * was how the injected Webflow markup reached the modal without being able to
 * call a hook. Every caller is a real component now, so the listener is gone;
 * `/request-audit` remains a real route for anyone who lands on it directly.
 */
export function useRequestAudit() {
  const context = useContext(RequestAuditContext);
  if (!context) throw new Error("useRequestAudit must be used within RequestAuditProvider");
  return context;
}

export function RequestAuditProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <RequestAuditContext.Provider value={{ open, close }}>
      {children}
      <RequestAuditModal isOpen={isOpen} onClose={close} />
    </RequestAuditContext.Provider>
  );
}
